import { isEqual } from 'es-toolkit';
import React, { useState } from 'react';
import { Lock, LockOpen } from '@mui/icons-material';
import { Box, Button, FormControl, FormLabel, Popover } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { connectorDisplayName } from '@paragon/layout-models';
import {
  hasCanUsePlatesPermission,
  hasHangerUpgradeAndAnalyzeFeatureFlag,
  hasTrussConversionPermission,
  hasTrussDesignPermission as hasTrussDesignPermissionSelector,
  isLoggedInUserEngineer,
} from '@paragon/login-tools';

import { areLockOptionsDefault, defaultLockOptions, LockOptions } from '../../../store/actions/lockOptionsActions';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import { isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import { inViewOnlyMode as componentIsViewOnlySelector } from '../../../store/selectors/viewOnlyModeSelectors';
import { setLockOptions } from '../../../store/slices/componentDesignsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { LockOptionsCheckboxes } from '../../misc/LockOptionsCheckboxes';
import { ToolbarIconButton } from '../top/ToolbarIconButton';

export function TrussLockOptionsButton() {
  const dispatch = useAppDispatch();

  const inLayoutMode = useAppSelector(isLayoutViewActive);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);

  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const hasTrussDesignPermission = useAppSelector(hasTrussDesignPermissionSelector);
  const componentIsViewOnly = useAppSelector(componentIsViewOnlySelector);
  const isEngineer = useAppSelector(isLoggedInUserEngineer);
  const hasPermissionToConvertTrusses = useAppSelector(hasTrussConversionPermission);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const allowHangerLocking = useAppSelector(hasHangerUpgradeAndAnalyzeFeatureFlag);

  if (inLayoutMode || activeComponentDesign == null || !hasTrussDesignPermission) return null;

  const lockOptions: LockOptions = {
    analogLocked: activeComponentDesign.analogLocked,
    hangersLocked: activeComponentDesign.hangersLocked,
    platingLocked: activeComponentDesign.platingLocked,
    loadingLocked: activeComponentDesign.loadingLocked,
    membersLocked: activeComponentDesign.membersLocked,
  };

  const lockedHangerNames = activeComponentDesign.lockedHangers.map((hangerGuid) => {
    const connector = activeComponentDesign.component.hangers.find(({ guid }) => guid === hangerGuid)?.connector;
    return connector == null ? 'unknown hanger' : connectorDisplayName(connector);
  });

  const lockedMemberNames = activeComponentDesign.lockedMembers.map(
    (memberGuid) =>
      activeComponentDesign.component.members.find((member) => member.guid === memberGuid)?.name ?? 'unknown member',
  );

  const lockedPlatePairNames = activeComponentDesign.lockedPlatePairs.map(
    (platePairGuid) =>
      activeComponentDesign.component.platePairs.find((platePair) => platePair.guid === platePairGuid)?.name ??
      'unknown plate pair',
  );

  const optionsAreDefault = areLockOptionsDefault(lockOptions);
  const anythingIsLocked = Object.values(lockOptions).some((locked) => locked);

  // Lock Everything must respect permissions, just like the existing category lock & unlock buttons (users cannot lock
  //   categories using the Lock Everything buttons that they cannot also lock & unlock using the category buttons).
  const canLockLoading = isEngineer || hasPermissionToConvertTrusses;
  const userCanLock: LockOptions = {
    analogLocked: canLockLoading || lockOptions.analogLocked,
    loadingLocked: canLockLoading || lockOptions.loadingLocked,
    platingLocked: canUsePlates || lockOptions.platingLocked,
    membersLocked: true, // everyone can lock members
    hangersLocked: allowHangerLocking || lockOptions.hangersLocked,
  };
  const anythingLockableIsUnlocked = !isEqual(userCanLock, lockOptions);

  return (
    <>
      <ToolbarIconButton
        title="Lock Options"
        icon={anythingIsLocked ? <Lock /> : <LockOpen />}
        disabled={componentIsViewOnly}
        onClick={(event) => {
          setPopoverOpen(true);
          setAnchorEl(event.currentTarget);
        }}
      />

      {isPopoverOpen && (
        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={() => void setPopoverOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 1 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Lock Options
                  <span
                    style={{
                      visibility: anythingLockableIsUnlocked ? 'visible' : 'hidden',
                    }}
                  >
                    <TooltipIconButton
                      title="Lock Everything"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() =>
                        dispatch(
                          setLockOptions({
                            componentDesignGuid: activeComponentDesign.guid,
                            lockOptions: userCanLock,
                          }),
                        )
                      }
                    >
                      <Lock />
                    </TooltipIconButton>
                  </span>
                  <span
                    style={{
                      visibility:
                        optionsAreDefault &&
                        lockedHangerNames.length === 0 &&
                        lockedMemberNames.length === 0 &&
                        lockedPlatePairNames.length === 0
                          ? 'hidden'
                          : 'visible',
                    }}
                  >
                    <TooltipIconButton
                      title="Unlock Everything"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() =>
                        dispatch(
                          setLockOptions({
                            componentDesignGuid: activeComponentDesign.guid,
                            lockOptions: defaultLockOptions,
                            forceIndividualUnlock: true,
                          }),
                        )
                      }
                    >
                      <LockOpen />
                    </TooltipIconButton>
                  </span>
                </FormLabel>
              </FormControl>
            </Box>

            <LockOptionsCheckboxes
              componentDesignGuid={activeComponentDesign.guid}
              lockOptions={lockOptions}
              lockedHangerNames={lockedHangerNames}
              lockedMemberNames={lockedMemberNames}
              lockedPlatePairNames={lockedPlatePairNames}
            />

            <Button fullWidth variant="contained" onClick={() => void setPopoverOpen(false)}>
              Close
            </Button>
          </Box>
        </Popover>
      )}
    </>
  );
}
